import { PDFDocument } from 'pdf-lib'

/**
 * Merge multiple PDF files into one
 * @param files - Array of PDF files as ArrayBuffers
 * @returns Merged PDF as Uint8Array
 */
export async function mergePdfs(files: ArrayBuffer[]): Promise<Uint8Array> {
  if (files.length === 0) {
    throw new Error('No files to merge')
  }

  if (files.length === 1) {
    return new Uint8Array(files[0])
  }

  // Create a new PDF document
  const mergedPdf = await PDFDocument.create()

  // Process each file
  for (const fileBytes of files) {
    const pdf = await PDFDocument.load(fileBytes)
    const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
    pages.forEach(page => mergedPdf.addPage(page))
  }

  // Save and return
  return await mergedPdf.save()
}

/**
 * Merge multiple PDF files with reordering
 * @param files - Array of PDF files with their order
 * @returns Merged PDF as Uint8Array
 */
export async function mergePdfsOrdered(files: { file: File; order: number }[]): Promise<Uint8Array> {
  if (files.length === 0) {
    throw new Error('No files to merge')
  }

  // Sort by order
  const sortedFiles = files.sort((a, b) => a.order - b.order)

  // Convert files to array buffers
  const arrayBuffers = await Promise.all(
    sortedFiles.map(({ file }) => fileToArrayBuffer(file))
  )

  return mergePdfs(arrayBuffers)
}

/**
 * Read file as ArrayBuffer
 */
async function fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

/**
 * Get total page count across multiple PDFs
 */
export async function getTotalPageCount(files: File[]): Promise<number> {
  let total = 0

  for (const file of files) {
    const arrayBuffer = await fileToArrayBuffer(file)
    const pdf = await PDFDocument.load(arrayBuffer)
    total += pdf.getPageCount()
  }

  return total
}
